import { useEffect, useRef, useState } from 'react';
import { Button, ProgressBar, Window, WindowContent, WindowHeader } from 'react95';
import { useLocale } from '../i18n/LocaleContext';
import { RESUME_PDF_FILENAME, RESUME_PDF_URL } from '../constants';

const MIN_ANIMATION_MS = 1800;

const downloadBlob = (blob, filename) => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
};

const wait = (ms) => new Promise((resolve) => {
  window.setTimeout(resolve, ms);
});

const DownloadDialog = ({ open, onClose }) => {
  const { t, lang } = useLocale();
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const abortRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setProgress(0);
      setError('');
      abortRef.current?.abort();
      abortRef.current = null;
      return undefined;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    let cancelled = false;
    let rafId = 0;
    const startedAt = performance.now();

    const animateTo = (target, durationMs) =>
      new Promise((resolve) => {
        const from = 0;
        const start = performance.now();

        const tick = (now) => {
          if (cancelled) {
            resolve();
            return;
          }

          const tNorm = Math.min(1, (now - start) / durationMs);
          const eased = 1 - (1 - tNorm) ** 2;
          setProgress(Math.round(from + (target - from) * eased));

          if (tNorm < 1) {
            rafId = requestAnimationFrame(tick);
          } else {
            resolve();
          }
        };

        rafId = requestAnimationFrame(tick);
      });

    const fetchPdf = async () => {
      const response = await fetch(RESUME_PDF_URL, { signal: controller.signal });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return response.blob();
    };

    const run = async () => {
      try {
        const [blob] = await Promise.all([
          fetchPdf(),
          animateTo(92, MIN_ANIMATION_MS),
        ]);

        if (cancelled) return;

        const elapsed = performance.now() - startedAt;
        if (elapsed < MIN_ANIMATION_MS) {
          await wait(MIN_ANIMATION_MS - elapsed);
        }

        if (cancelled) return;

        setProgress(100);
        downloadBlob(blob, RESUME_PDF_FILENAME[lang] || RESUME_PDF_FILENAME.ru);
        await wait(400);
        if (!cancelled) onClose();
      } catch (err) {
        if (cancelled || err?.name === 'AbortError') return;
        setError(t.downloadError);
        setProgress(0);
      }
    };

    run();

    return () => {
      cancelled = true;
      controller.abort();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [open, onClose, t.downloadError, lang]);

  if (!open) {
    return null;
  }

  return (
    <div className="download-overlay" role="presentation">
      <Window
        className="download-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="download-dialog-title"
      >
        <WindowHeader>
          <span id="download-dialog-title">{t.downloadTitle}</span>
        </WindowHeader>
        <WindowContent>
          <p style={{ marginBottom: '12px' }}>
            {error || t.downloadStatus}
          </p>
          {!error && <ProgressBar value={progress} variant="tile" />}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
            <Button onClick={onClose}>
              {t.close}
            </Button>
          </div>
        </WindowContent>
      </Window>
    </div>
  );
};

export default DownloadDialog;
